/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useState } from 'react';
import { ActivityIndicator, RefreshControl, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors } from '../../shared/theme/ticketColors';
import type { SaleDetail } from '../domain/salesTypes';
import { displayPaymentMethodLabel } from '../infra/salesPaymentLabels';
import {
  formatCashSessionLabel,
  formatSaleCreatedAt,
  formatSaleDisplayRef,
  formatMercadoPagoPaymentDetail,
  formatSaleChannelLabel,
  isOnlineSaleChannel,
  spanishSaleStatusLabel,
  buildSaleStatusChips,
  resolveSalePaymentLinePresentation,
} from '../application/salePresentation';
import {
  formatSaleDetailLinePromoHint,
  hasSaleDetailPromotions,
  summarizeSaleDetailLines,
  summarizeSaleDetailDiscounts,
} from '../application/saleDetailPromotionPresentation';
import { formatMoney as formatPromoMoney } from '../../shared/format/public-api';
import { StatusPill } from '../../shared/surface-cards/StatusPill';
import { ChipWrapRow } from '../../shared/ui/ChipWrapRow';
import { saleDetailStyles as styles } from './saleDetail.styles';
import { SaleFiscalComprobantePanel } from '../../fiscal/components/SaleFiscalComprobantePanel';
import type { FiscalComprobanteExportInput } from '../../fiscal/application/fiscalComprobanteFormat';
import { mapSaleItemsToFiscalExport } from '../../fiscal/infra-api';
import { ModalHeroHeader } from '../../shared/modals/ModalHeroHeader'
import { ModalFormScroll } from '../../shared/modals/ModalFormScroll'
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { SaleDetailItemsBreakdown } from './SaleDetailItemsBreakdown';
import { SaleDetailPromotionsSummary } from './SaleDetailPromotionsSummary';
import { SaleAttachmentsSection } from './SaleAttachmentsSection';
import { SaleDetailCommissionsSection } from './SaleDetailCommissionsSection';
import { formatQty } from '@shared/quantity';
import { formatMoney } from '../../shared/format/public-api';

export type SaleDetailContentProps = {
  loading: boolean;
  detail: SaleDetail | null;
  paymentMethodNames: Record<string, string>;
  paymentMethodNamesLoaded?: boolean;
  diningTableName?: string | null;
  canVoid?: boolean;
  voidLoading?: boolean;
  onVoidPress?: () => void;
  canCollectCustomer?: boolean;
  onCollectCustomer?: () => void;
  onFiscalRetryComplete?: () => void;
  canCollectMpQr?: boolean;
  onCollectMpQr?: () => void;
  onViewLinkedOrder?: () => void;
  canReprint?: boolean;
  reprintLoading?: boolean;
  onReprintPress?: () => void;
  footerMode?: 'inline' | 'sticky';
  /** Panel derecho del workspace web: grid ancho y padding de panel. */
  workspaceMode?: boolean;
  /**
   * Rail de secciones (desk, proto ventas-ux-v1): Resumen / Ítems / Cobro /
   * Fiscal / Adjuntos / Comisiones — las acciones quedan siempre visibles en
   * Resumen, arriba del fold.
   */
  railMode?: boolean;
  /** Mobile clean design full-width sheet layout. */
  mobileClean?: boolean;
  refreshing?: boolean;
  onRefresh?: () => void;
  onClose?: () => void;
  /** Mostrar sección de comisiones (capability + permission gated) */
  showCommissions?: boolean;
  /** Mostrar atribución de empleados por ítem (capability gated) */
  showAttribution?: boolean;
  /** ID del usuario logueado — para filtrar comisiones si canViewOthers es false */
  currentUserId?: string | null;
  /** Si puede ver comisiones de otros empleados */
  canViewOthersCommissions?: boolean;
  /** Callback para navegar a Planes de Comisión */
  onNavigateToCommissionPlans?: () => void;
};

export function SaleDetailContent({
  loading,
  detail,
  paymentMethodNames,
  paymentMethodNamesLoaded = true,
  diningTableName,
  canVoid = false,
  voidLoading = false,
  onVoidPress,
  canCollectCustomer = false,
  onCollectCustomer,
  onFiscalRetryComplete,
  canCollectMpQr = false,
  onCollectMpQr,
  onViewLinkedOrder,
  canReprint = false,
  reprintLoading = false,
  onReprintPress,
  footerMode = 'inline',
  workspaceMode = false,
  railMode = false,
  mobileClean = false,
  refreshing = false,
  onRefresh,
  onClose,
  showCommissions = false,
  showAttribution = false,
  currentUserId = null,
  canViewOthersCommissions = true,
  onNavigateToCommissionPlans,
}: SaleDetailContentProps) {
  const insets = useSafeAreaInsets();

  if (loading) {
    return (
      <View style={styles.mobileLoadingWrap}>
        <ActivityIndicator size="large" color={ticketColors.ink2} />
        <Text style={[styles.caption, { marginTop: spacing.sm }]}>Cargando…</Text>
      </View>
    );
  }

  if (!detail) {
    return (
      <View style={{ padding: spacing.lg }}>
        <Text style={styles.caption}>No encontré la venta.</Text>
      </View>
    );
  }

  if (mobileClean) {
    return (
      <MobileCleanDetail
        detail={detail}
        paymentMethodNames={paymentMethodNames}
        paymentMethodNamesLoaded={paymentMethodNamesLoaded}
        diningTableName={diningTableName}
        canVoid={canVoid}
        voidLoading={voidLoading}
        onVoidPress={onVoidPress}
        canCollectCustomer={canCollectCustomer}
        onCollectCustomer={onCollectCustomer}
        canCollectMpQr={canCollectMpQr}
        onCollectMpQr={onCollectMpQr}
        onViewLinkedOrder={onViewLinkedOrder}
        canReprint={canReprint}
        reprintLoading={reprintLoading}
        onReprintPress={onReprintPress}
        onFiscalRetryComplete={onFiscalRetryComplete}
        insets={insets}
        refreshing={refreshing}
        onRefresh={onRefresh}
        onClose={onClose}
        showCommissions={showCommissions}
        showAttribution={showAttribution}
        currentUserId={currentUserId}
        canViewOthersCommissions={canViewOthersCommissions}
        onNavigateToCommissionPlans={onNavigateToCommissionPlans}
      />
    );
  }

  // ── Existing (wide/workspace) layout ──
  const isVoided = detail.status === 'voided';
  const refLabel = formatSaleDisplayRef({
    saleId: detail.id,
    counterNumber: detail.counterNumber,
  });
  const showStickyFooter = footerMode === 'sticky';
  const pendingLine =
    !isVoided && detail.pendingAmount != null && detail.pendingAmount > 0
      ? `${formatMoney(detail.pendingAmount)}`
      : null;
  const paidLine =
    !isVoided && detail.paidAmount != null && detail.paymentStatus !== 'paid'
      ? `${formatMoney(detail.paidAmount)}`
      : null;
  const cashSessionLabel = formatCashSessionLabel(detail.cashSessionId);
  const showFiscalPanel = detail.fiscalMode !== 'non_fiscal';
  const paymentMethodIds = detail.payments.map((p) => p.methodId);
  const summaryChips = buildSaleStatusChips({
    paymentStatus: detail.paymentStatus,
    fiscalMode: detail.fiscalMode,
    fiscalStatus: detail.fiscalStatus,
    synced: detail.synced,
    isVoided,
    paymentMethodIds,
    invoiceStatus: detail.invoiceStatus,
    invoiceCae: detail.invoiceCae,
  });
  const showPaymentTotalsInContext =
    detail.payments.length === 0 && (paidLine != null || pendingLine != null);
  const showPromotionBreakdown = hasSaleDetailPromotions(detail);
  const discountTotal = Number(detail.discountTotal ?? 0);
  const tipAmount = Math.max(0, Number(detail.tipAmount ?? 0) || 0);
  const subtotal = detail.subtotal ?? (showPromotionBreakdown ? detail.total - tipAmount + discountTotal : null);
  const contextRows = [
    { label: 'Canal', value: formatSaleChannelLabel(detail.channel) },
    detail.cashierDisplayName ? { label: 'Operador', value: detail.cashierDisplayName } : null,
    detail.primaryStaffDisplayName
      ? { label: 'Atendió', value: detail.primaryStaffDisplayName }
      : null,
    tipAmount > 0
      ? { label: 'Propina', value: `${formatMoney(tipAmount)}` }
      : null,
    detail.fiadoCustomerName ? { label: 'Cliente a cuenta', value: detail.fiadoCustomerName } : null,
    detail.customerName ? { label: 'Cliente', value: detail.customerName } : null,
    cashSessionLabel ? { label: 'Caja', value: cashSessionLabel } : null,
    detail.diningTableId ? { label: 'Mesa', value: diningTableName ?? detail.diningTableId } : null,
    showPaymentTotalsInContext && paidLine ? { label: 'Cobrado', value: paidLine } : null,
    showPaymentTotalsInContext && pendingLine ? { label: 'Pendiente', value: pendingLine } : null,
  ].filter(Boolean) as Array<{ label: string; value: string }>;

  const fiscalExportContext: FiscalComprobanteExportInput | null =
    detail.fiscalMode === 'non_fiscal'
      ? null
      : {
          saleId: detail.id,
          saleRef: refLabel,
          saleTotal: detail.total,
          saleCreatedAt: detail.createdAt,
          items: mapSaleItemsToFiscalExport(detail.items, tipAmount),
          cae: detail.invoiceCae ?? '',
          issuedAt: detail.invoiceIssuedAt,
          caeExpiration: detail.invoiceCaeExpiration,
          qrUrl: detail.invoiceQrUrl,
          qrPayload: detail.invoiceQrPayload,
          invoiceType: detail.invoiceType,
          pointOfSale: detail.invoicePointOfSale,
          invoiceNumber: detail.invoiceNumber,
          buyerName: detail.invoiceBuyerName,
          buyerCuit: detail.invoiceBuyerCuit,
          emitterBusinessName: detail.emitterBusinessName,
          emitterCuit: detail.emitterCuit,
          netAmount: detail.invoiceNetAmount,
          ivaAmount: detail.invoiceIvaAmount,
          diningTableName: diningTableName ?? null,
        };

  const voidButton =
    canVoid && !isVoided && typeof onVoidPress === 'function' ? (
      <TouchableOpacity
        style={[modalExperience.detailActionBtn, modalExperience.detailActionBtnDanger]}
        onPress={onVoidPress}
        disabled={voidLoading}
        accessibilityRole="button"
        accessibilityLabel="Anular venta"
        testID="ventas.void-sale"
      >
        <Ionicons name="trash-outline" size={18} color={ticketColors.bad} />
        {voidLoading ? (
          <ActivityIndicator color={ticketColors.bad} />
        ) : (
          <Text style={modalExperience.detailActionTextDanger}>Anular venta</Text>
        )}
      </TouchableOpacity>
    ) : null;

  const collectCustomerButton =
    canCollectCustomer && !isVoided && typeof onCollectCustomer === 'function' ? (
      <TouchableOpacity
        style={[modalExperience.detailActionBtn, modalExperience.detailActionBtnPrimary]}
        onPress={onCollectCustomer}
        accessibilityRole="button"
        accessibilityLabel="Registrar cobro a cuenta"
      >
        <Ionicons name="cash-outline" size={18} color="#FFFFFF" />
        <Text style={modalExperience.detailActionTextPrimary}>
          Cobrar a cuenta{pendingLine ? ` (${pendingLine})` : ''}
        </Text>
      </TouchableOpacity>
    ) : null;

  const collectMpQrButton =
    canCollectMpQr && !isVoided && typeof onCollectMpQr === 'function' ? (
      <TouchableOpacity
        style={[modalExperience.detailActionBtn, modalExperience.detailActionBtnPrimary]}
        onPress={onCollectMpQr}
        accessibilityRole="button"
        accessibilityLabel="Cobrar con QR Mercado Pago"
      >
        <Ionicons name="qr-code-outline" size={18} color="#FFFFFF" />
        <Text style={modalExperience.detailActionTextPrimary}>
          Cobrar con QR Mercado Pago{pendingLine ? ` (${pendingLine})` : ''}
        </Text>
      </TouchableOpacity>
    ) : null;

  const reprintButton =
    canReprint && typeof onReprintPress === 'function' ? (
      <TouchableOpacity
        style={modalExperience.detailActionBtn}
        onPress={onReprintPress}
        disabled={reprintLoading}
        accessibilityRole="button"
        accessibilityLabel="Reimprimir ticket"
        testID="ventas.reprint-ticket"
      >
        <Ionicons name="print-outline" size={18} color={ticketColors.ink} />
        {reprintLoading ? (
          <ActivityIndicator color={ticketColors.ink2} />
        ) : (
          <Text style={modalExperience.detailActionText}>Reimprimir ticket</Text>
        )}
      </TouchableOpacity>
    ) : null;

  const onlineChannel = isOnlineSaleChannel(detail.channel);
  const viewOrderButton =
    detail.customerOrderId && typeof onViewLinkedOrder === 'function' ? (
      <TouchableOpacity
        style={[
          modalExperience.detailActionBtn,
          onlineChannel ? modalExperience.detailActionBtnPrimary : null,
        ]}
        onPress={onViewLinkedOrder}
        accessibilityRole="button"
        accessibilityLabel="Ver pedido"
      >
        <Ionicons
          name="storefront-outline"
          size={18}
          color={onlineChannel ? ticketColors.goldInk : ticketColors.ink}
        />
        <Text
          style={
            onlineChannel
              ? modalExperience.detailActionTextPrimary
              : modalExperience.detailActionText
          }
        >
          Ver pedido
        </Text>
      </TouchableOpacity>
    ) : null;

  const footerActions =
    reprintButton || collectMpQrButton || collectCustomerButton || voidButton || viewOrderButton ? (
      <View style={modalExperience.detailActionsPanel}>
        <View style={modalExperience.detailActionsGrid}>
          {reprintButton}
          {viewOrderButton}
          {collectMpQrButton}
          {collectCustomerButton}
          {voidButton}
        </View>
      </View>
    ) : null;

  // ── Bloques de sección (compartidos entre scroll único y railMode) ──
  const heroBlock = (
    <>
      <ModalHeroHeader
        kicker={`${formatMoney(detail.total)}`}
        subtitle={[
          formatSaleCreatedAt(detail.createdAt),
          `Ref. ${refLabel}`,
          showPromotionBreakdown && subtotal != null
            ? `Subtotal ${formatPromoMoney(subtotal)}${discountTotal > 0 ? ` · Promos −${formatPromoMoney(discountTotal)}` : ''}`
            : null,
        ].filter(Boolean).join(' · ')}
        badgeText={spanishSaleStatusLabel(detail.status)}
      />

      {!isVoided ? (
        <ChipWrapRow style={styles.saleDetailChipRow}>
          <StatusPill
            label={formatSaleChannelLabel(detail.channel)}
            tone={onlineChannel ? 'service' : 'neutral'}
            compact
          />
          {summaryChips.map((chip) => (
            <StatusPill key={chip.key} label={chip.label} tone={chip.tone} compact />
          ))}
        </ChipWrapRow>
      ) : null}

      {contextRows.length > 0 ? (
        <View style={modalExperience.detailMetaGrid}>
          {contextRows.map((row) => (
            <View key={row.label} style={modalExperience.detailKvRow}>
              <Text style={modalExperience.detailMetaLabel}>{row.label}</Text>
              <Text style={modalExperience.detailKvValueStrong}>{row.value}</Text>
            </View>
          ))}
        </View>
      ) : null}

      {detail.fiadoCustomerName && !isVoided && (detail.pendingAmount ?? 0) > 0 ? (
        <View style={styles.fiadoWarnBox}>
          <Text style={styles.fiadoWarnText}>
            {`Fiado a ${detail.fiadoCustomerName} — queda en su cuenta hasta que pague.`}
          </Text>
        </View>
      ) : null}

      {isVoided ? (
        <View style={styles.voidedBanner}>
          <Text style={styles.voidedBannerTitle}>Venta anulada</Text>
          <Text style={styles.voidedBannerText}>
            Esta venta no cuenta en caja ni en informes. Los cobros quedaron anulados.
            {showCommissions ? ' Las comisiones también fueron anuladas.' : ''}
          </Text>
          <Text style={styles.voidedBannerText}>
            {[
              detail.voidedAt ? `Anulada el ${formatSaleCreatedAt(detail.voidedAt)}` : null,
              detail.voidedByDisplayName ? `por ${detail.voidedByDisplayName}` : null,
            ]
              .filter(Boolean)
              .join(' ')}
          </Text>
          {detail.voidReason ? (
            <Text style={styles.voidedBannerText}>Motivo: {detail.voidReason}</Text>
          ) : null}
        </View>
      ) : null}
    </>
  );

  const fiscalBlock = showFiscalPanel ? (
    <SaleFiscalComprobantePanel
      saleId={detail.id}
      branchId={detail.branchId}
      fiscalMode={detail.fiscalMode}
      fiscalStatus={detail.fiscalStatus}
      saleStatus={detail.status}
      invoiceStatus={detail.invoiceStatus}
      invoiceCae={detail.invoiceCae}
      invoiceIssuedAt={detail.invoiceIssuedAt}
      invoiceQrUrl={detail.invoiceQrUrl}
      exportContext={fiscalExportContext}
      emitterFiscalEnvironment={detail.emitterFiscalEnvironment}
      creditNoteStatus={detail.creditNoteStatus}
      creditNoteCae={detail.creditNoteCae}
      creditNoteIssuedAt={detail.creditNoteIssuedAt}
      onRetryComplete={onFiscalRetryComplete}
    />
  ) : null;

  const itemsBlock = (
    <View style={styles.saleDetailSection}>
      <SaleDetailItemsBreakdown items={detail.items} discounts={detail.discounts ?? []} showAttribution={showAttribution} />
      <SaleDetailPromotionsSummary discounts={detail.discounts ?? []} />
    </View>
  );

  const cobroBlock =
    detail.payments.length === 0 ? (
      <Text style={styles.caption}>Sin cobro.</Text>
    ) : (
      <View style={modalExperience.listContainer}>
        {detail.payments.map((p, index) => {
          const methodLabel = paymentMethodNamesLoaded
            ? displayPaymentMethodLabel(p.methodId, paymentMethodNames)
            : '…';
          const lineStatus = resolveSalePaymentLinePresentation(p.status, p.methodId);
          const isMpQr = p.methodId === 'mercadopago_qr';
          const mpDetail = isMpQr
            ? formatMercadoPagoPaymentDetail({ reference: p.reference, metadata: p.metadata })
            : null;
          return (
            <View
              key={p.id}
              style={[
                modalExperience.listRow,
                index === detail.payments.length - 1 && styles.saleDetailPaymentBlockLast,
              ]}
            >
              <View style={styles.saleDetailPaymentHeader}>
                <Text style={modalExperience.listRowTitle}>
                  {formatMoney(p.amount)}
                </Text>
                <StatusPill label={lineStatus.label} tone={lineStatus.tone} compact />
              </View>
              <Text style={modalExperience.listMeta}>{methodLabel}</Text>
              {isMpQr && mpDetail?.operationNumber ? (
                <Text style={modalExperience.listMetaMuted}>Nº operación: {mpDetail.operationNumber}</Text>
              ) : null}
              {!isMpQr && p.reference ? (
                <Text style={modalExperience.listMetaMuted}>Ref. {p.reference}</Text>
              ) : null}
            </View>
          );
        })}
      </View>
    );

  const attachmentsBlock = (
    <SaleAttachmentsSection saleId={detail.id} saleVoided={detail.status === 'voided'} />
  );

  const commissionsBlock = showCommissions ? (
    <SaleDetailCommissionsSection
      saleId={detail.id}
      saleVoided={detail.status === 'voided'}
      currentUserId={currentUserId}
      canViewOthers={canViewOthersCommissions}
      canViewCommissions={showCommissions}
      onNavigateToPlans={onNavigateToCommissionPlans}
    />
  ) : null;

  // ── Rail de secciones (desk, proto ventas-ux-v1) ──
  if (railMode) {
    const railSections: SaleDetailRailSection[] = [
      { key: 'resumen', label: 'Resumen' },
      { key: 'items', label: 'Ítems', count: detail.items.length },
      { key: 'cobro', label: 'Cobro', count: detail.payments.length },
      ...(showFiscalPanel ? [{ key: 'fiscal', label: 'Fiscal' } as SaleDetailRailSection] : []),
      { key: 'adjuntos', label: 'Adjuntos' },
      ...(showCommissions ? [{ key: 'comisiones', label: 'Comisiones' } as SaleDetailRailSection] : []),
    ];
    const railContent: Record<string, React.ReactNode> = {
      resumen: (
        <>
          {heroBlock}
          {footerActions ? (
            <>
              <Text style={modalExperience.formSectionTitle}>Acciones</Text>
              {footerActions}
            </>
          ) : null}
        </>
      ),
      items: itemsBlock,
      cobro: cobroBlock,
      fiscal: fiscalBlock,
      adjuntos: attachmentsBlock,
      comisiones: commissionsBlock,
    };
    return (
      <SaleDetailRailLayout sections={railSections} contentBySection={railContent} />
    );
  }

  return (
    <View style={[styles.contentWrap, workspaceMode && styles.contentWrapWorkspace]}>
      <ModalFormScroll
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={[
          styles.scrollContent,
          workspaceMode && styles.scrollContentWorkspace,
          showStickyFooter ? styles.scrollContentWithFooter : null,
        ]}
        refreshing={refreshing}
        onRefresh={onRefresh}
      >
        {heroBlock}

        {isVoided ? (
          <View style={styles.voidReasonBlock}>
            {detail.voidedAt ? (
              <>
                <Text style={styles.fieldLabel}>Anulada el</Text>
                <Text style={styles.voidReasonText}>{formatSaleCreatedAt(detail.voidedAt)}</Text>
              </>
            ) : null}
            {detail.voidReason ? (
              <>
                <Text style={styles.fieldLabel}>Motivo de anulación</Text>
                <Text style={styles.voidReasonText}>{detail.voidReason}</Text>
              </>
            ) : null}
            {detail.voidedByDisplayName ? (
              <Text style={styles.caption}>Por: {detail.voidedByDisplayName}</Text>
            ) : null}
          </View>
        ) : null}

        {fiscalBlock}

        <Text style={modalExperience.formSectionTitle}>Ítems</Text>
        {itemsBlock}

        <Text style={modalExperience.formSectionTitle}>Cobro</Text>
        {cobroBlock}

        {attachmentsBlock}

        {commissionsBlock}

        {footerMode === 'inline' ? footerActions : null}
      </ModalFormScroll>

      {showStickyFooter && footerActions ? (
        <View style={[styles.footerVoid, { paddingBottom: Math.max(insets.bottom, spacing.md) }]}>
          {footerActions}
        </View>
      ) : null}
    </View>
  );
}

// ── Rail de secciones (desk) ──

type SaleDetailRailSection = { key: string; label: string; count?: number };

function SaleDetailRailLayout({
  sections,
  contentBySection,
}: {
  sections: SaleDetailRailSection[];
  contentBySection: Record<string, React.ReactNode>;
}) {
  const [activeSection, setActiveSection] = useState<string>(sections[0]?.key ?? 'resumen');
  const effective = sections.some((s) => s.key === activeSection)
    ? activeSection
    : (sections[0]?.key ?? 'resumen');

  return (
    <View style={railStyles.wrap}>
      <View style={railStyles.rail}>
        {sections.map((section) => {
          const isActive = effective === section.key;
          return (
            <TouchableOpacity
              key={section.key}
              style={[railStyles.railItem, isActive && railStyles.railItemActive]}
              onPress={() => setActiveSection(section.key)}
              activeOpacity={0.7}
              accessibilityRole="button"
              accessibilityLabel={section.label}
              accessibilityState={{ selected: isActive }}
            >
              <Text style={[railStyles.railLabel, isActive && railStyles.railLabelActive]}>
                {section.label}
              </Text>
              {section.count != null ? (
                <Text style={[railStyles.railCount, isActive && railStyles.railCountActive]}>
                  {section.count}
                </Text>
              ) : null}
            </TouchableOpacity>
          );
        })}
      </View>
      <ScrollView
        style={railStyles.pane}
        contentContainerStyle={railStyles.paneContent}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        {contentBySection[effective] ?? null}
      </ScrollView>
    </View>
  );
}

const railStyles = StyleSheet.create({
  wrap: {
    flex: 1,
    flexDirection: 'row',
    minHeight: 0,
  },
  rail: {
    width: 148,
    flexShrink: 0,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.sm,
    gap: 2,
    borderRightWidth: 1,
    borderRightColor: ticketColors.ruleSoft,
  },
  railItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.xs,
    borderRadius: 8,
    paddingHorizontal: spacing.sm,
    paddingVertical: 9,
  },
  railItemActive: {
    backgroundColor: ticketColors.gold + '25',
  },
  railLabel: {
    fontSize: 13,
    fontWeight: '500',
    color: ticketColors.ink2,
    flexShrink: 1,
  },
  railLabelActive: {
    fontWeight: '700',
    color: ticketColors.ink,
  },
  railCount: {
    fontSize: 11,
    fontWeight: '600',
    color: ticketColors.ink3,
  },
  railCountActive: {
    color: ticketColors.ink2,
  },
  pane: {
    flex: 1,
    minWidth: 0,
  },
  paneContent: {
    padding: 20,
    gap: spacing.md,
    flexGrow: 1,
  },
});

// ── Mobile clean detail component ──

type MobileCleanProps = {
  detail: SaleDetail;
  paymentMethodNames: Record<string, string>;
  paymentMethodNamesLoaded: boolean;
  diningTableName?: string | null;
  canVoid: boolean;
  voidLoading: boolean;
  onVoidPress?: () => void;
  canCollectCustomer: boolean;
  onCollectCustomer?: () => void;
  canCollectMpQr: boolean;
  onCollectMpQr?: () => void;
  onViewLinkedOrder?: () => void;
  onFiscalRetryComplete?: () => void;
  canReprint?: boolean;
  reprintLoading?: boolean;
  onReprintPress?: () => void;
  insets: { bottom: number };
  refreshing?: boolean;
  onRefresh?: () => void;
  onClose?: () => void;
  showCommissions?: boolean;
  showAttribution?: boolean;
  currentUserId?: string | null;
  canViewOthersCommissions?: boolean;
  onNavigateToCommissionPlans?: () => void;
};

function MobileCleanDetail({
  detail,
  paymentMethodNames,
  paymentMethodNamesLoaded,
  diningTableName,
  canVoid,
  voidLoading,
  onVoidPress,
  canCollectCustomer,
  onCollectCustomer,
  canCollectMpQr,
  onCollectMpQr,
  onViewLinkedOrder,
  onFiscalRetryComplete,
  canReprint = false,
  reprintLoading = false,
  onReprintPress,
  insets,
  refreshing = false,
  onRefresh,
  onClose,
  showCommissions = false,
  showAttribution = false,
  currentUserId = null,
  canViewOthersCommissions = true,
  onNavigateToCommissionPlans,
}: MobileCleanProps) {
  const isVoided = detail.status === 'voided';
  const refLabel = formatSaleDisplayRef({
    saleId: detail.id,
    counterNumber: detail.counterNumber,
  });
  const onlineChannel = isOnlineSaleChannel(detail.channel);
  const cashSessionLabel = formatCashSessionLabel(detail.cashSessionId);
  const showFiscalPanel = detail.fiscalMode !== 'non_fiscal';
  const paymentMethodIds = detail.payments.map((p) => p.methodId);
  const summaryChips = buildSaleStatusChips({
    paymentStatus: detail.paymentStatus,
    fiscalMode: detail.fiscalMode,
    fiscalStatus: detail.fiscalStatus,
    synced: detail.synced,
    isVoided,
    paymentMethodIds,
    invoiceStatus: detail.invoiceStatus,
    invoiceCae: detail.invoiceCae,
  });
  const showPromotionBreakdown = hasSaleDetailPromotions(detail);
  const discountTotal = Number(detail.discountTotal ?? 0);
  const tipAmount = Math.max(0, Number(detail.tipAmount ?? 0) || 0);
  const subtotal = detail.subtotal ?? (showPromotionBreakdown ? detail.total - tipAmount + discountTotal : null);
  const paidAmount = detail.paidAmount ?? 0;
  const pendingAmount = detail.pendingAmount ?? 0;
  const itemRows = summarizeSaleDetailLines(detail.items);
  const discountRows = summarizeSaleDetailDiscounts(detail.discounts ?? []);

  const contextRows = [
    { label: 'Canal', value: formatSaleChannelLabel(detail.channel) },
    detail.primaryStaffDisplayName
      ? { label: 'Atendió', value: detail.primaryStaffDisplayName }
      : null,
    tipAmount > 0
      ? { label: 'Propina', value: `${formatMoney(tipAmount)}` }
      : null,
    detail.fiadoCustomerName ? { label: 'Cliente a cuenta', value: detail.fiadoCustomerName } : null,
    detail.customerName ? { label: 'Cliente', value: detail.customerName } : null,
    cashSessionLabel ? { label: 'Caja', value: cashSessionLabel } : null,
    detail.diningTableId ? { label: 'Mesa', value: diningTableName ?? detail.diningTableId } : null,
  ].filter(Boolean) as Array<{ label: string; value: string }>;

  const fiscalExportContext: FiscalComprobanteExportInput | null =
    detail.fiscalMode === 'non_fiscal'
      ? null
      : {
          saleId: detail.id,
          saleRef: refLabel,
          saleTotal: detail.total,
          saleCreatedAt: detail.createdAt,
          items: mapSaleItemsToFiscalExport(detail.items, tipAmount),
          cae: detail.invoiceCae ?? '',
          issuedAt: detail.invoiceIssuedAt,
          caeExpiration: detail.invoiceCaeExpiration,
          qrUrl: detail.invoiceQrUrl,
          qrPayload: detail.invoiceQrPayload,
          invoiceType: detail.invoiceType,
          pointOfSale: detail.invoicePointOfSale,
          invoiceNumber: detail.invoiceNumber,
          buyerName: detail.invoiceBuyerName,
          buyerCuit: detail.invoiceBuyerCuit,
          emitterBusinessName: detail.emitterBusinessName,
          emitterCuit: detail.emitterCuit,
          netAmount: detail.invoiceNetAmount,
          ivaAmount: detail.invoiceIvaAmount,
          diningTableName: diningTableName ?? null,
        };

  const pendingLine =
    !isVoided && pendingAmount > 0
      ? `${formatMoney(pendingAmount)}`
      : null;

  // Footer buttons
  const showVoidBtn = canVoid && !isVoided && typeof onVoidPress === 'function';
  const showCustomerBtn = canCollectCustomer && !isVoided && typeof onCollectCustomer === 'function';
  const showMpQrBtn = canCollectMpQr && !isVoided && typeof onCollectMpQr === 'function';
  const showViewOrderBtn = Boolean(detail.customerOrderId) && typeof onViewLinkedOrder === 'function';
  const showReprintBtn = canReprint && typeof onReprintPress === 'function';
  const hasFooter = showVoidBtn || showCustomerBtn || showMpQrBtn || showViewOrderBtn || showReprintBtn;

  return (
    <View style={{ flex: 1 }}>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={[styles.mobileScrollContent, { paddingBottom: hasFooter ? 80 : spacing.xxl }]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        refreshControl={
          onRefresh ? (
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              colors={[ticketColors.gold]}
              tintColor={ticketColors.gold}
            />
          ) : undefined
        }
      >
        {/* Hero */}
        <View style={styles.mobileHero}>
          <Text style={styles.mobileHeroKicker}>
            {`Ref. ${refLabel} · ${formatSaleCreatedAt(detail.createdAt)}`}
          </Text>
          <Text style={[styles.mobileHeroTitle, isVoided && styles.mobileHeroTitleVoided]}>
            {`${formatMoney(detail.total)}`}
          </Text>
          <Text style={styles.mobileHeroSubtitle}>
            {[
              detail.cashierDisplayName ? `Operador: ${detail.cashierDisplayName}` : null,
              detail.primaryStaffDisplayName ? `Atendió: ${detail.primaryStaffDisplayName}` : null,
              tipAmount > 0 ? `Propina: ${formatMoney(tipAmount)}` : null,
              detail.fiadoCustomerName ? `A cuenta: ${detail.fiadoCustomerName}` : null,
              detail.customerName ? `Cliente: ${detail.customerName}` : null,
            ].filter(Boolean).join(' · ')}
          </Text>
        </View>

        {/* Status pills */}
        {!isVoided ? (
          <View style={styles.mobilePillRow}>
            <StatusPill
              label={formatSaleChannelLabel(detail.channel)}
              tone={onlineChannel ? 'service' : 'neutral'}
              compact
            />
            {summaryChips.map((chip) => (
              <StatusPill key={chip.key} label={chip.label} tone={chip.tone} compact />
            ))}
          </View>
        ) : null}

        {/* Voided banner */}
        {isVoided ? (
          <View style={styles.mobileVoidedBanner}>
            <Text style={styles.mobileVoidedBannerTitle}>Venta anulada</Text>
            <Text style={styles.mobileVoidedBannerText}>
              Esta venta no cuenta en caja ni en informes. Los cobros quedaron anulados.
              {showCommissions ? ' Las comisiones también fueron anuladas.' : ''}
            </Text>
            {detail.voidedAt ? (
              <Text style={styles.mobileVoidedMeta}>
                {`Anulada el ${formatSaleCreatedAt(detail.voidedAt)}`}
                {detail.voidedByDisplayName ? ` por ${detail.voidedByDisplayName}` : ''}
              </Text>
            ) : null}
            {detail.voidReason ? (
              <Text style={styles.mobileVoidedMeta}>Motivo: {detail.voidReason}</Text>
            ) : null}
          </View>
        ) : null}

        {/* Metric card */}
        <View style={styles.mobileMetricCard}>
          <View style={styles.mobileMetricRow}>
            <Text style={styles.mobileMetricLabel}>Total</Text>
            <Text
              style={[
                styles.mobileMetricValue,
                isVoided && { textDecorationLine: 'line-through', color: ticketColors.ink2 },
              ]}
            >
              {`${formatMoney(detail.total)}`}
            </Text>
          </View>
          {isVoided ? (
            <View style={[styles.mobileMetricRow, styles.mobileMetricRowDivider]}>
              <Text style={styles.mobileMetricLabel}>Cobrado</Text>
              <Text
                style={[
                  styles.mobileMetricValue,
                  { textDecorationLine: 'line-through', color: ticketColors.ink2 },
                ]}
              >
                $0
              </Text>
            </View>
          ) : (
            <>
              <View style={[styles.mobileMetricRow, styles.mobileMetricRowDivider]}>
                <Text style={styles.mobileMetricLabel}>Cobrado</Text>
                <Text
                  style={[
                    styles.mobileMetricValue,
                    paidAmount > 0 && styles.mobileMetricValueSuccess,
                  ]}
                >
                  {`${formatMoney(paidAmount)}`}
                </Text>
              </View>
              {pendingAmount > 0 ? (
                <View style={[styles.mobileMetricRow, styles.mobileMetricRowDivider]}>
                  <Text style={styles.mobileMetricLabel}>Saldo pendiente</Text>
                  <Text
                    style={[styles.mobileMetricValue, styles.mobileMetricValueDanger]}
                  >
                    {`${formatMoney(pendingAmount)}`}
                  </Text>
                </View>
              ) : null}
              {subtotal != null && showPromotionBreakdown ? (
                <View style={[styles.mobileMetricRow, styles.mobileMetricRowDivider]}>
                  <Text style={styles.mobileMetricLabel}>Subtotal</Text>
                  <Text style={styles.mobileMetricValue}>
                    {`${formatMoney(subtotal)}`}
                  </Text>
                </View>
              ) : null}
              {discountTotal > 0 ? (
                <View style={[styles.mobileMetricRow, styles.mobileMetricRowDivider]}>
                  <Text style={styles.mobileMetricLabel}>Descuentos</Text>
                  <Text
                    style={[styles.mobileMetricValue, styles.mobileMetricValueWarning]}
                  >
                    {`−${formatMoney(discountTotal)}`}
                  </Text>
                </View>
              ) : null}
            </>
          )}
        </View>

        {/* Context */}
        {contextRows.length > 0 ? (
          <View style={styles.mobileContextGrid}>
            {contextRows.map((row) => (
              <View key={row.label} style={styles.mobileContextRow}>
                <Text style={styles.mobileContextLabel}>{row.label}</Text>
                <Text style={styles.mobileContextValue}>{row.value}</Text>
              </View>
            ))}
          </View>
        ) : null}

        {detail.fiadoCustomerName && !isVoided && pendingAmount > 0 ? (
          <View style={styles.fiadoWarnBox}>
            <Text style={styles.fiadoWarnText}>
              {`Fiado a ${detail.fiadoCustomerName} — queda en su cuenta hasta que pague.`}
            </Text>
          </View>
        ) : null}

        {/* Items */}
        <View style={styles.mobileDivider} />
        <View style={styles.mobileSection}>
          <Text style={styles.mobileSectionTitle}>{`Ítems (${detail.items.length})`}</Text>
          {detail.items.length === 0 ? (
            <Text style={styles.mobileEmptyStateText}>Sin ítems.</Text>
          ) : (
            detail.items.map((item) => {
              const row = itemRows.find((v) => v.lineId === item.id);
              if (!row) return null;
              const lineHint = formatSaleDetailLinePromoHint(item, detail.discounts ?? []);
              return (
                <View key={item.id} style={styles.mobileItemCard}>
                  <View style={styles.mobileItemTop}>
                    <Text style={styles.mobileItemName} numberOfLines={2}>
                      {item.label ?? item.productId}
                    </Text>
                    <Text style={styles.mobileItemQty}>{`× ${formatQty(row.quantity, item.unitOfMeasure)}`}</Text>
                  </View>
                  {item.sku ? <Text style={styles.mobileItemSku}>SKU: {item.sku}</Text> : null}
                  {lineHint ? (
                    <Text style={styles.mobileItemPromoHint} numberOfLines={1}>
                      {lineHint}
                    </Text>
                  ) : null}
                  {row.discountTotal > 0 ? (
                    <Text style={styles.mobileItemPriceList}>
                      {formatPromoMoney(row.listTotal)}
                    </Text>
                  ) : null}
                  <Text style={styles.mobileItemPrice}>
                    {formatPromoMoney(row.netTotal)}
                  </Text>
                  {showAttribution && (item.performedByDisplayName || item.soldByDisplayName) ? (
                    <View style={styles.mobileItemAttribution}>
                      {item.performedByDisplayName ? (
                        <Text style={styles.mobileItemAttributionText}>
                          Realiza: {item.performedByDisplayName}
                        </Text>
                      ) : null}
                      {item.soldByDisplayName ? (
                        <Text style={styles.mobileItemAttributionText}>
                          Vende: {item.soldByDisplayName}
                        </Text>
                      ) : null}
                    </View>
                  ) : null}
                </View>
              );
            })
          )}

          {/* Promotions summary */}
          {discountRows.length > 0 ? (
            <View>
              {discountRows.map((discount) => (
                <View key={discount.id} style={styles.mobileDiscountRow}>
                  <Text style={styles.mobileDiscountLabel}>{discount.promotionName}</Text>
                  <Text style={styles.mobileDiscountAmount}>
                    {`−${formatPromoMoney(discount.discountAmount)}`}
                  </Text>
                </View>
              ))}
            </View>
          ) : null}
        </View>

        {/* Cobro */}
        <View style={styles.mobileDivider} />
        <View style={styles.mobileSection}>
          <Text style={styles.mobileSectionTitle}>Cobro</Text>
          {detail.payments.length === 0 ? (
            <View style={styles.mobileEmptyState}>
              <Text style={styles.mobileEmptyStateText}>Sin cobro.</Text>
            </View>
          ) : (
            detail.payments.map((p) => {
              const methodLabel = paymentMethodNamesLoaded
                ? displayPaymentMethodLabel(p.methodId, paymentMethodNames)
                : '…';
              const lineStatus = resolveSalePaymentLinePresentation(p.status, p.methodId);
              const isMpQr = p.methodId === 'mercadopago_qr';
              const mpDetail = isMpQr
                ? formatMercadoPagoPaymentDetail({ reference: p.reference, metadata: p.metadata })
                : null;
              const isPending = lineStatus.tone === 'warning';
              const isVoidedPayment = lineStatus.tone === 'danger';
              return (
                <View
                  key={p.id}
                  style={[
                    styles.mobilePaymentItem,
                    isVoidedPayment && styles.mobilePaymentItemVoided,
                    isPending && styles.mobilePaymentItemPending,
                  ]}
                >
                  <View style={styles.mobilePaymentTop}>
                    <Text
                      style={[
                        styles.mobilePaymentTitle,
                        isVoidedPayment && styles.mobilePaymentTitleVoided,
                      ]}
                    >
                      {`${formatMoney(p.amount)}`}
                    </Text>
                    <StatusPill label={lineStatus.label} tone={lineStatus.tone} compact />
                  </View>
                  <Text style={styles.mobilePaymentMeta}>{methodLabel}</Text>
                  {isMpQr && mpDetail?.operationNumber ? (
                    <Text style={styles.mobilePaymentMeta}>
                      {`Nº operación: ${mpDetail.operationNumber}`}
                    </Text>
                  ) : null}
                  {!isMpQr && p.reference ? (
                    <Text style={styles.mobilePaymentMeta}>{`Ref. ${p.reference}`}</Text>
                  ) : null}
                </View>
              );
            })
          )}
        </View>

        {/* Comprobantes de la venta (cheque / transferencia) */}
        <View style={styles.mobileDivider} />
        <View style={styles.mobileSection}>
          <SaleAttachmentsSection
            saleId={detail.id}
            saleVoided={detail.status === 'voided'}
            titleStyle={styles.mobileSectionTitle}
          />
        </View>

        {/* Comisiones */}
        {showCommissions ? (
          <>
            <View style={styles.mobileDivider} />
            <View style={styles.mobileSection}>
              <SaleDetailCommissionsSection
                saleId={detail.id}
                saleVoided={detail.status === 'voided'}
                currentUserId={currentUserId}
                canViewOthers={canViewOthersCommissions}
                canViewCommissions={showCommissions}
                onNavigateToPlans={onNavigateToCommissionPlans}
              />
            </View>
          </>
        ) : null}

        {/* Fiscal panel — después de cobro, como en el proto */}
        {showFiscalPanel ? (
          <>
            <View style={styles.mobileDivider} />
            <View style={styles.mobileSection}>
              <Text style={styles.mobileSectionTitle}>Comprobante fiscal</Text>
              <SaleFiscalComprobantePanel
                saleId={detail.id}
                branchId={detail.branchId}
                fiscalMode={detail.fiscalMode}
                fiscalStatus={detail.fiscalStatus}
                saleStatus={detail.status}
                invoiceStatus={detail.invoiceStatus}
                invoiceCae={detail.invoiceCae}
                invoiceIssuedAt={detail.invoiceIssuedAt}
                invoiceQrUrl={detail.invoiceQrUrl}
                exportContext={fiscalExportContext}
                emitterFiscalEnvironment={detail.emitterFiscalEnvironment}
                creditNoteStatus={detail.creditNoteStatus}
                creditNoteCae={detail.creditNoteCae}
                creditNoteIssuedAt={detail.creditNoteIssuedAt}
                onRetryComplete={onFiscalRetryComplete}
              />
            </View>
          </>
        ) : null}
      </ScrollView>

      {/* Footer */}
      {hasFooter ? (
        <View style={[styles.mobileFooter, { paddingBottom: Math.max(insets.bottom, 12) }]}>
          {showViewOrderBtn ? (
            <TouchableOpacity
              style={[
                styles.mobileFooterBtn,
                onlineChannel
                  ? styles.mobileFooterBtnPrimary
                  : styles.mobileFooterBtnSecondary,
              ]}
              onPress={onViewLinkedOrder}
              activeOpacity={0.7}
              accessibilityRole="button"
              accessibilityLabel="Ver pedido"
            >
              <Ionicons
                name="storefront-outline"
                size={18}
                color={onlineChannel ? ticketColors.goldInk : ticketColors.ink}
              />
              <Text
                style={[
                  styles.mobileFooterBtnText,
                  onlineChannel
                    ? styles.mobileFooterBtnTextPrimary
                    : styles.mobileFooterBtnTextSecondary,
                ]}
              >
                Ver pedido
              </Text>
            </TouchableOpacity>
          ) : null}
          {showReprintBtn ? (
            <TouchableOpacity
              style={[styles.mobileFooterBtn, styles.mobileFooterBtnSecondary]}
              onPress={onReprintPress}
              disabled={reprintLoading}
              activeOpacity={0.7}
              accessibilityRole="button"
              accessibilityLabel="Reimprimir ticket"
              testID="ventas.reprint-ticket"
            >
              <Ionicons name="print-outline" size={18} color={ticketColors.ink} />
              {reprintLoading ? (
                <ActivityIndicator color={ticketColors.ink2} />
              ) : (
                <Text style={[styles.mobileFooterBtnText, styles.mobileFooterBtnTextSecondary]}>
                  Reimprimir ticket
                </Text>
              )}
            </TouchableOpacity>
          ) : null}
          {showVoidBtn ? (
            <TouchableOpacity
              style={[styles.mobileFooterBtn, styles.mobileFooterBtnDanger]}
              onPress={onVoidPress}
              disabled={voidLoading}
              activeOpacity={0.7}
              accessibilityRole="button"
              accessibilityLabel="Anular venta"
              testID="ventas.void-sale"
            >
              <Ionicons name="trash-outline" size={18} color={ticketColors.bad} />
              {voidLoading ? (
                <ActivityIndicator color={ticketColors.bad} />
              ) : (
                <Text style={[styles.mobileFooterBtnText, styles.mobileFooterBtnTextDanger]}>
                  Anular venta
                </Text>
              )}
            </TouchableOpacity>
          ) : null}
          {showMpQrBtn ? (
            <TouchableOpacity
              style={[styles.mobileFooterBtn, styles.mobileFooterBtnPrimary]}
              onPress={onCollectMpQr}
              activeOpacity={0.7}
              accessibilityRole="button"
              accessibilityLabel="Cobrar con QR Mercado Pago"
            >
              <Ionicons name="qr-code-outline" size={18} color="#FFFFFF" />
              <Text style={[styles.mobileFooterBtnText, styles.mobileFooterBtnTextPrimary]}>
                {`Cobrar con QR Mercado Pago${pendingLine ? ` (${pendingLine})` : ''}`}
              </Text>
            </TouchableOpacity>
          ) : null}
          {showCustomerBtn ? (
            <TouchableOpacity
              style={[styles.mobileFooterBtn, styles.mobileFooterBtnPrimary]}
              onPress={onCollectCustomer}
              activeOpacity={0.7}
              accessibilityRole="button"
              accessibilityLabel="Registrar cobro a cuenta"
            >
              <Ionicons name="cash-outline" size={18} color="#FFFFFF" />
              <Text style={[styles.mobileFooterBtnText, styles.mobileFooterBtnTextPrimary]}>
                {`Cobrar a cuenta${pendingLine ? ` (${pendingLine})` : ''}`}
              </Text>
            </TouchableOpacity>
          ) : null}
        </View>
      ) : isVoided ? (
        <View style={[styles.mobileFooter, { paddingBottom: Math.max(insets.bottom, 12) }]}>
          <TouchableOpacity
            style={[styles.mobileFooterBtn, styles.mobileFooterBtnSecondary]}
            onPress={onClose}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel="Cerrar"
          >
            <Text style={[styles.mobileFooterBtnText, styles.mobileFooterBtnTextSecondary]}>
              Cerrar
            </Text>
          </TouchableOpacity>
        </View>
      ) : null}
    </View>
  );
}

